import { useMemo, useState } from "react"
import type { inferRouterOutputs } from "@trpc/server"
import { RefreshCw, Save, Trash2 } from "lucide-react"
import { parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs"
import toast from "react-hot-toast"

import { gatewaySubjectApplicationId, parseGatewaySubjectApplicationId } from "@aix/jsutils/src/aiGatewayPolicy"
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useLocalStorage,
} from "@aix/ui"

import type { AppRouter } from "../../../aixinternal-api/src/router/_app"
import { MfaGate } from "../components/auth/MfaGate"
import { supabase } from "../lib/supabase"
import { trpc } from "../lib/trpc"

const usd = (micro: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 6 }).format(
    micro / 1_000_000,
  )
const ENVIRONMENTS = ["all", "dev", "staging", "prod"] as const
const STATUSES = ["all", "reserved", "uncertain", "settled", "failed"] as const
type Environment = (typeof ENVIRONMENTS)[number]
type RequestStatus = (typeof STATUSES)[number]
type RouterOutputs = inferRouterOutputs<AppRouter>
type GatewayDirectoryData = RouterOutputs["aiGateway"]["directory"]
type GatewayUsageData = RouterOutputs["aiGateway"]["usage"]
type GatewayRequestRow = RouterOutputs["aiGateway"]["requests"]["rows"][number]
type GatewayPolicyRow = RouterOutputs["aiGateway"]["policies"]["rows"][number]
type GatewayRequestMetrics = NonNullable<GatewayRequestRow["metrics"]>
export const formatSettlement = (status: GatewayRequestRow["status"], settledAt: string | null): string => {
  if (status === "reserved" || status === "uncertain") return "Not settled"
  if (settledAt) return settledAt
  return "Unknown"
}

export interface GatewayUrlFilters {
  subject: string
  app: string
  environment: Environment
  model: string
  status: RequestStatus
  from: string
  to: string
}
export interface GatewaySavedView {
  id: string
  name: string
  filters: GatewayUrlFilters
}
const parseUtcCalendarDate = (date: string): Date | undefined => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined
  const [year, month, day] = date.split("-").map(Number)
  const parsed = new Date(Date.UTC(year, month - 1, day))
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day
    ? parsed
    : undefined
}
export const toExclusiveUtc = (date: string): string | undefined => {
  const parsed = parseUtcCalendarDate(date)
  if (!parsed) return undefined
  parsed.setUTCDate(parsed.getUTCDate() + 1)
  return parsed.toISOString()
}
const toStartUtc = (date: string): string | undefined => {
  return parseUtcCalendarDate(date)?.toISOString()
}
export const validateDateRange = (from: string, to: string): string | null => {
  if (!from && !to) return null
  if ((from && !to) || (!from && to)) return "Choose both a start and end date."
  const start = toStartUtc(from)
  const end = toExclusiveUtc(to)
  if (!start || !end) return "Dates must use the YYYY-MM-DD format."
  return start < end ? null : "The start date must be before the end date."
}

type GatewayDirectoryPerson = GatewayDirectoryData["developers"][number]
type GatewayDirectoryApp = GatewayDirectoryData["applications"][number]

const FILTER_PARSERS = {
  subject: parseAsString.withDefault(""),
  app: parseAsString.withDefault(""),
  environment: parseAsStringLiteral(ENVIRONMENTS).withDefault("all"),
  model: parseAsString.withDefault(""),
  status: parseAsStringLiteral(STATUSES).withDefault("all"),
  from: parseAsString.withDefault(""),
  to: parseAsString.withDefault(""),
}
const formatMs = (value: number | null | undefined): string => (value == null ? "Unknown" : `${Math.round(value)} ms`)
const formatAge = (createdAt: string): string => {
  const timestamp = Date.parse(createdAt)
  if (Number.isNaN(timestamp)) return "Unknown age"
  const ageMinutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000))
  if (ageMinutes < 60) return `${ageMinutes}m old`
  const ageHours = Math.floor(ageMinutes / 60)
  if (ageHours < 24) return `${ageHours}h ${ageMinutes % 60}m old`
  return `${Math.floor(ageHours / 24)}d ${ageHours % 24}h old`
}
const averageMetric = (rows: readonly GatewayRequestRow[], key: keyof GatewayRequestMetrics): number | null => {
  const values = rows.map((row) => row.metrics?.[key]).filter((value): value is number => value != null)
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null
}
const policyScopeLabel = (
  policy: GatewayPolicyRow,
  developers: readonly GatewayDirectoryPerson[],
  applications: readonly GatewayDirectoryApp[],
): string => {
  if (policy.scope === "subject") {
    return developers.find((item) => item.id === policy.scopeId)?.name ?? `Unknown developer (${policy.scopeId})`
  }
  if (policy.scope === "application") {
    return applications.find((item) => item.id === policy.scopeId)?.name ?? `Unknown application (${policy.scopeId})`
  }
  if (policy.scope !== "subject_application") return policy.scopeId
  const value = parseGatewaySubjectApplicationId(policy.scopeId)
  if (!value) return `Unknown developer/application (${policy.scopeId})`
  const subject = developers.find((item) => item.id === value[0])?.name ?? `Unknown developer (${value[0]})`
  const application = applications.find((item) => item.id === value[1])?.name ?? `Unknown application (${value[1]})`
  return `${subject} · ${application}`
}

export default function AiGatewayScreen({ readOnly = false }: { readOnly?: boolean } = {}) {
  const [mfaMode, setMfaMode] = useState<"enroll" | "challenge" | null>(null)
  const [checkingMfa, setCheckingMfa] = useState(false)
  const [mfaError, setMfaError] = useState<string | null>(null)
  const vidmaxPendingId =
    typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("vidmaxPending")
  const gatewayPendingId =
    typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("gatewayPending")
  const validGatewayPendingId = Boolean(gatewayPendingId && /^[A-Za-z0-9_-]{43,128}$/u.test(gatewayPendingId))
  const [newScope, setNewScope] = useState<GatewayPolicyRow["scope"]>("subject")
  const [newEnvironment, setNewEnvironment] = useState<Exclude<Environment, "all">>("dev")
  const [newPolicySubject, setNewPolicySubject] = useState("")
  const [newPolicyApp, setNewPolicyApp] = useState("")
  const identity = trpc.aiGateway.identity.useQuery()
  const appConnection = trpc.aiGateway.devAppConnection.useQuery(
    { pendingId: gatewayPendingId ?? "" },
    { enabled: validGatewayPendingId && Boolean(identity.data), retry: false },
  )
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null)
  const [newViewName, setNewViewName] = useState("")
  const [savedViews, setSavedViews] = useLocalStorage<GatewaySavedView[]>(
    `aix-ai-gateway-saved-views:${identity.data?.subject ?? "anonymous"}`,
    [],
  )
  const [queryFilters, setQueryFilters] = useQueryStates(FILTER_PARSERS)
  const admin = identity.data?.role === "admin"
  const canManage = admin && !readOnly
  const dateError = validateDateRange(queryFilters.from, queryFilters.to)
  const filters = useMemo(() => {
    const next: {
      subject?: string
      app?: string
      environment?: Exclude<Environment, "all">
      model?: string
      status?: Exclude<RequestStatus, "all">
      from?: string
      to?: string
    } = {}
    if (queryFilters.subject) next.subject = queryFilters.subject
    if (queryFilters.app) next.app = queryFilters.app
    if (queryFilters.environment !== "all") next.environment = queryFilters.environment
    if (queryFilters.model) next.model = queryFilters.model
    if (queryFilters.status !== "all") next.status = queryFilters.status
    if (!dateError && queryFilters.from) next.from = toStartUtc(queryFilters.from)
    if (!dateError && queryFilters.to) next.to = toExclusiveUtc(queryFilters.to)
    return next
  }, [dateError, queryFilters])
  const directory = trpc.aiGateway.directory.useQuery(undefined, { enabled: Boolean(identity.data) })
  const usage = trpc.aiGateway.usage.useQuery(filters, { enabled: Boolean(identity.data) && !dateError })
  const requests = trpc.aiGateway.requests.useQuery(filters, { enabled: Boolean(identity.data) && !dateError })
  const policies = trpc.aiGateway.policies.useQuery(undefined, { enabled: Boolean(identity.data) })
  const utils = trpc.useUtils()
  const savePolicy = trpc.aiGateway.setPolicy.useMutation({
    onSuccess: async () => {
      toast.success("Budget policy saved")
      await utils.aiGateway.invalidate()
    },
    onError: (error) => toast.error(error.message),
  })
  const reconcile = trpc.aiGateway.reconcile.useMutation({
    onSuccess: async () => {
      toast.success("Charge reconciled")
      await utils.aiGateway.invalidate()
    },
    onError: (failure) => toast.error(failure.message),
  })
  const approveVidmax = trpc.aiGateway.approveVidmaxDevConnect.useMutation({
    onSuccess: () => toast.success("VidMax connected. Return to VidMax to continue."),
    onError: (failure) => toast.error(failure.message),
  })
  const approveApp = trpc.aiGateway.approveDevAppConnect.useMutation({
    onSuccess: async () => {
      toast.success("DEV AI connected. Return to your app to continue.")
      await appConnection.refetch()
    },
    onError: (failure) => toast.error(failure.message),
  })
  let connectionAction = "Approve connection"
  if (approveApp.isLoading || appConnection.data?.status === "approving") connectionAction = "Connecting…"
  if (approveApp.isSuccess || appConnection.data?.status === "approved")
    connectionAction = "Connected — return to your app"
  const usageData: GatewayUsageData | undefined = usage.data
  const requestRows: GatewayRequestRow[] = requests.data?.rows ?? []
  const policyRows: GatewayPolicyRow[] = policies.data?.rows ?? []
  const directoryData: GatewayDirectoryData | undefined = directory.data
  const rows = usageData?.rows ?? []
  const days = [...new Set(rows.map((row) => row.day))].sort()
  const daily = days.map((day) => ({
    day,
    amount: rows
      .filter((row) => row.day === day)
      .reduce((sum, row) => sum + row.costMicroUsd + row.reservedMicroUsd, 0),
  }))
  const modelBreakdown = [...new Set(rows.map((row) => row.model))].sort().map((model) => {
    const modelRows = rows.filter((row) => row.model === model)
    return {
      model,
      requests: modelRows.reduce((sum, row) => sum + row.requests, 0),
      costMicroUsd: modelRows.reduce((sum, row) => sum + row.costMicroUsd, 0),
      reservedMicroUsd: modelRows.reduce((sum, row) => sum + row.reservedMicroUsd, 0),
    }
  })
  const largest = Math.max(1, ...daily.map((day) => day.amount))
  const selected = requestRows.find((row) => row.id === selectedRequest)
  const error =
    identity.error ?? appConnection.error ?? directory.error ?? usage.error ?? requests.error ?? policies.error
  const needsMfa = error?.message === "Complete MFA before using AI access"
  const unsettled = requestRows.filter((row) => row.status === "reserved" || row.status === "uncertain")
  const measuredFailures =
    usageData?.metrics?.failures ??
    requestRows.filter((row) => row.status === "failed" || row.failureCode !== null).length
  const currentFilters = queryFilters as GatewayUrlFilters
  const latency = {
    gatewayMs: usageData?.metrics?.averageGatewayMs ?? averageMetric(requestRows, "gatewayMs"),
    providerMs: usageData?.metrics?.averageProviderMs ?? averageMetric(requestRows, "providerMs"),
    totalMs: usageData?.metrics?.averageTotalMs ?? averageMetric(requestRows, "totalMs"),
  }

  async function completeMfa() {
    setCheckingMfa(true)
    setMfaError(null)
    try {
      const { data, error: assuranceError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      if (assuranceError || !data) throw assuranceError ?? new Error("Unable to check MFA. Please try again.")
      if (data.currentLevel === "aal2") {
        await utils.aiGateway.invalidate()
        return
      }
      if (readOnly && data.nextLevel !== "aal2") {
        setMfaError("Existing MFA required. Sign in with an account that already has MFA configured.")
        return
      }
      setMfaMode(data.nextLevel === "aal2" ? "challenge" : "enroll")
    } catch (failure) {
      setMfaError(failure instanceof Error ? failure.message : "Unable to open MFA. Please try again.")
    } finally {
      setCheckingMfa(false)
    }
  }
  const updateFilter = (key: keyof GatewayUrlFilters, value: string) => {
    void setQueryFilters({ [key]: value || null })
  }
  const saveCurrentView = () => {
    const name = newViewName.trim()
    if (!name) return
    setSavedViews([
      ...savedViews.filter((view) => view.name !== name),
      { id: crypto.randomUUID(), name, filters: currentFilters },
    ])
    setNewViewName("")
    toast.success("Saved view created")
  }
  const createPolicy = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canManage) return
    const data = new FormData(event.currentTarget)
    const resolvedScopeId =
      newScope === "environment"
        ? newEnvironment
        : newScope === "subject_application"
          ? gatewaySubjectApplicationId(newPolicySubject.trim(), newPolicyApp.trim())
          : newScope === "subject"
            ? newPolicySubject.trim()
            : newPolicyApp.trim()
    savePolicy.mutate({
      scope: newScope,
      scopeId: resolvedScopeId,
      environment: newEnvironment,
      dailyLimitMicroUsd: Math.round(Number(data.get("daily")) * 1_000_000),
      monthlyLimitMicroUsd: Math.round(Number(data.get("monthly")) * 1_000_000),
      alertThresholdPercent: Math.round(Number(data.get("threshold") ?? 80)),
      enabled: true,
    })
  }
  const policyScopeInputs = (
    <>
      <div className="space-y-2">
        <Label>Developer</Label>
        <Select
          value={newPolicySubject || "none"}
          onValueChange={(value) => setNewPolicySubject(value === "none" ? "" : value)}
          disabled={newScope !== "subject" && newScope !== "subject_application"}
        >
          <SelectTrigger>
            <SelectValue placeholder="Choose developer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Choose developer</SelectItem>
            {(directoryData?.developers ?? []).map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name} · {item.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Application</Label>
        <Select
          value={newPolicyApp || "none"}
          onValueChange={(value) => setNewPolicyApp(value === "none" ? "" : value)}
          disabled={newScope !== "application" && newScope !== "subject_application"}
        >
          <SelectTrigger>
            <SelectValue placeholder="Choose application" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Choose application</SelectItem>
            {(directoryData?.applications ?? []).map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {newScope === "environment" && (
        <p className="self-end text-sm text-muted-foreground">Applies to the entire {newEnvironment} environment.</p>
      )}
    </>
  )
  if (mfaMode)
    return (
      <div>
        <Button variant="ghost" className="m-4" onClick={() => setMfaMode(null)}>
          Back to AI Gateway
        </Button>
        <MfaGate
          mode={mfaMode}
          onVerified={async () => {
            setMfaMode(null)
            await utils.aiGateway.invalidate()
          }}
        />
      </div>
    )

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">AI Gateway</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {admin ? "Organization usage, requests and budgets" : "Your AI usage, requests and allowances"}
          </p>
        </div>
        <Button variant="outline" onClick={() => void utils.aiGateway.invalidate()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>
      <p className="rounded-md border bg-secondary p-3 text-sm">
        Work use only. Prompts and responses are recorded and visible to authorized administrators. Provider credentials
        are never displayed.
      </p>
      {!readOnly && gatewayPendingId && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="text-base">Connect DEV AI</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!validGatewayPendingId && (
              <p role="alert" className="text-sm text-destructive">
                Invalid connection link. Start a new connection from your app.
              </p>
            )}
            {validGatewayPendingId && appConnection.data && (
              <>
                <p className="text-sm text-muted-foreground">
                  Connect <strong className="text-foreground">{appConnection.data.app}</strong> with your verified MFA
                  session. The credential stays on the server and applies only to this product’s DEV inference.
                </p>
                <Button
                  type="button"
                  disabled={approveApp.isLoading || approveApp.isSuccess || appConnection.data.status !== "pending"}
                  onClick={() => approveApp.mutate({ pendingId: gatewayPendingId })}
                >
                  {connectionAction}
                </Button>
              </>
            )}
            {validGatewayPendingId && !appConnection.data && (
              <p className="text-sm text-muted-foreground">
                {appConnection.error
                  ? "Connection unavailable. Start a new connection from your app."
                  : "Checking connection…"}
              </p>
            )}
          </CardContent>
        </Card>
      )}
      {!readOnly && vidmaxPendingId && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="text-base">Connect VidMax DEV AI</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Approve this one-time connection with your verified MFA session. The credential stays on the server and is
              limited to VidMax DEV inference.
            </p>
            <Button
              type="button"
              disabled={approveVidmax.isLoading || approveVidmax.isSuccess}
              onClick={() => approveVidmax.mutate({ pendingId: vidmaxPendingId })}
            >
              {approveVidmax.isLoading ? "Connecting…" : approveVidmax.isSuccess ? "Connected" : "Approve connection"}
            </Button>
          </CardContent>
        </Card>
      )}
      {error && (
        <div role="alert" className="rounded-md border border-destructive p-4 text-destructive">
          {needsMfa ? (
            <>
              <Button
                variant="link"
                className="h-auto p-0 text-destructive"
                disabled={checkingMfa}
                onClick={() => void completeMfa()}
              >
                {checkingMfa ? "Opening MFA…" : error.message}
              </Button>
              <p className="mt-1 text-sm">Verify with your authenticator to continue. You’ll return here afterward.</p>
            </>
          ) : (
            error.message
          )}
          {mfaError && <p className="mt-2 text-sm">{mfaError}</p>}
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
          <p className="text-sm text-muted-foreground">
            Filters are URL-backed and safe to share with authorized teammates.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label>Application</Label>
              <Select
                value={currentFilters.app || "all"}
                onValueChange={(value) => updateFilter("app", value === "all" ? "" : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All applications" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All applications</SelectItem>
                  {(directoryData?.applications ?? []).map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {admin && (
              <div className="space-y-2">
                <Label>Developer</Label>
                <Select
                  value={currentFilters.subject || "all"}
                  onValueChange={(value) => updateFilter("subject", value === "all" ? "" : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All developers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All developers</SelectItem>
                    {(directoryData?.developers ?? []).map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} · {item.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Environment</Label>
              <Select value={currentFilters.environment} onValueChange={(value) => updateFilter("environment", value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENVIRONMENTS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {value === "all" ? "All environments" : value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Model</Label>
              <Select
                value={currentFilters.model || "all"}
                onValueChange={(value) => updateFilter("model", value === "all" ? "" : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All models" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All models</SelectItem>
                  {(directoryData?.models ?? []).map((model) => (
                    <SelectItem key={model} value={model}>
                      {model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="space-y-2 text-sm">
              From (UTC)
              <Input
                type="date"
                value={currentFilters.from}
                onChange={(event) => updateFilter("from", event.target.value)}
              />
            </label>
            <label className="space-y-2 text-sm">
              Through (UTC)
              <Input
                type="date"
                value={currentFilters.to}
                onChange={(event) => updateFilter("to", event.target.value)}
              />
            </label>
            <div className="space-y-2">
              <Label>Request status</Label>
              <Select value={currentFilters.status} onValueChange={(value) => updateFilter("status", value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {value === "all" ? "All statuses" : value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {dateError && (
              <p className="self-end text-sm text-destructive" role="alert">
                {dateError}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="max-w-xs"
              aria-label="Saved view name"
              value={newViewName}
              onChange={(event) => setNewViewName(event.target.value)}
              placeholder="Name this view"
            />
            <Button variant="outline" onClick={saveCurrentView} disabled={!newViewName.trim()}>
              <Save className="mr-2 h-4 w-4" />
              Save view
            </Button>
            {savedViews.map((view) => (
              <span key={view.id} className="inline-flex items-center gap-1 rounded border px-2 py-1 text-sm">
                <button type="button" onClick={() => void setQueryFilters(view.filters)}>
                  {view.name}
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${view.name}`}
                  onClick={() => setSavedViews(savedViews.filter((item) => item.id !== view.id))}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </CardContent>
      </Card>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Finalized spend</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl">
            {usageData ? usd(usageData.totals.costMicroUsd) : "—"}
            <p className="mt-1 text-xs text-muted-foreground">Selected range</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Reserved / uncertain</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl">
            {usageData ? usd(usageData.totals.reservedMicroUsd) : "—"}
            <p className="mt-1 text-xs text-muted-foreground">Selected range</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Requests</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl">{usageData?.totals.requests.toLocaleString() ?? "—"}</CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Models used</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl">{usageData ? new Set(rows.map((row) => row.model)).size : "—"}</CardContent>
        </Card>
      </div>
      <Tabs defaultValue="usage">
        <TabsList>
          <TabsTrigger value="usage">Usage & models</TabsTrigger>
          <TabsTrigger value="requests">Requests & telemetry</TabsTrigger>
          <TabsTrigger value="policies">Budgets & limits</TabsTrigger>
        </TabsList>
        <TabsContent value="usage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Daily budget consumption</CardTitle>
              <p className="text-sm text-muted-foreground">
                Finalized spend plus reservations for the selected range. Current budget cards use server-provided UTC
                totals.
              </p>
            </CardHeader>
            <CardContent>
              {usage.isLoading && <p>Loading usage…</p>}
              {usageData && daily.length === 0 && (
                <p className="text-muted-foreground">No recorded usage for this selection.</p>
              )}
              <div className="space-y-3">
                {daily.map((day) => (
                  <div key={day.day} className="grid grid-cols-[7rem_1fr_6rem] items-center gap-3 text-sm">
                    <span>{day.day.slice(0, 10)}</span>
                    <div className="h-5 rounded bg-secondary">
                      <div
                        className="h-full rounded bg-primary"
                        style={{ width: `${(100 * day.amount) / largest}%` }}
                      />
                    </div>
                    <span className="text-right">{usd(day.amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Model breakdown</CardTitle>
              <p className="text-sm text-muted-foreground">Aggregated only from usage rows in the selected range.</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-secondary">
                    <tr>
                      {["Model", "Requests", "Spend", "Reserved"].map((title) => (
                        <th key={title} className="p-3">
                          {title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modelBreakdown.map((item) => (
                      <tr key={item.model} className="border-t">
                        <td className="p-3">{item.model}</td>
                        <td className="p-3">{item.requests}</td>
                        <td className="p-3">{usd(item.costMicroUsd)}</td>
                        <td className="p-3">{usd(item.reservedMicroUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-left text-sm">
              <caption className="p-3 text-left text-muted-foreground">Daily model breakdown from usage rows</caption>
              <thead className="bg-secondary">
                <tr>
                  {["Date", "Developer", "Application", "Environment", "Model", "Requests", "Spend", "Reserved"].map(
                    (title) => (
                      <th key={title} className="p-3">
                        {title}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.day}-${index}`} className="border-t">
                    <td className="p-3">{row.day.slice(0, 10)}</td>
                    <td className="p-3 font-mono text-xs">
                      {directoryData?.developers.find((item) => item.id === row.subject)?.name ?? row.subject}
                    </td>
                    <td className="p-3">
                      {directoryData?.applications.find((item) => item.id === row.app)?.name ?? row.app}
                    </td>
                    <td className="p-3">{row.environment}</td>
                    <td className="p-3">{row.model}</td>
                    <td className="p-3">{row.requests}</td>
                    <td className="p-3">{usd(row.costMicroUsd)}</td>
                    <td className="p-3">{usd(row.reservedMicroUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="requests" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Gateway latency</CardTitle>
              </CardHeader>
              <CardContent className="text-xl">
                {formatMs(latency.gatewayMs)}
                <p className="text-xs text-muted-foreground">Measured rows average</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Provider latency</CardTitle>
              </CardHeader>
              <CardContent className="text-xl">
                {formatMs(latency.providerMs)}
                <p className="text-xs text-muted-foreground">Measured rows average</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Total latency</CardTitle>
              </CardHeader>
              <CardContent className="text-xl">
                {formatMs(latency.totalMs)}
                <p className="text-xs text-muted-foreground">Measured rows average</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Measured failures</CardTitle>
              </CardHeader>
              <CardContent className="text-xl">
                {usageData?.metrics
                  ? measuredFailures.toLocaleString()
                  : requestRows.length
                    ? measuredFailures.toLocaleString()
                    : "Unknown"}
                <p className="text-xs text-muted-foreground">
                  {usageData?.metrics
                    ? `${usageData.metrics.measuredRequests.toLocaleString()} measured requests`
                    : "Returned rows only; broader declined traffic is unknown"}
                </p>
              </CardContent>
            </Card>
          </div>
          {(unsettled.length > 0 || usageData?.metrics?.unsettled) && (
            <Card>
              <CardHeader>
                <CardTitle>Unsettled charges</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {usageData?.metrics
                    ? `${usageData.metrics.unsettled.toLocaleString()} unsettled request(s) in the selected range.`
                    : "Reserved and uncertain requests remain held until provider charge evidence is reconciled."}
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {unsettled.map((row) => (
                  <div
                    key={row.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded border p-3 text-sm"
                  >
                    <span>
                      {row.id} · {row.status} · {formatAge(row.createdAt)}
                    </span>
                    <span>{usd(row.estimatedMicroUsd)} estimated hold</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {requests.isLoading && <p>Loading requests…</p>}
          {requests.data?.rows.length === 0 && <p className="text-muted-foreground">No requests recorded.</p>}
          <p className="text-sm text-muted-foreground">Latest 100 requests matching these filters.</p>
          <div className="space-y-2">
            {requestRows.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => setSelectedRequest(row.id)}
                className="flex w-full flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-left hover:bg-secondary"
              >
                <span>
                  <span className="font-medium">{row.model}</span>
                  <span className="ml-3 text-xs text-muted-foreground">
                    {row.provider} · {directoryData?.applications.find((item) => item.id === row.app)?.name ?? row.app}{" "}
                    · {directoryData?.developers.find((item) => item.id === row.subject)?.name ?? row.subject} ·{" "}
                    {row.createdAt}
                  </span>
                </span>
                <span className="flex items-center gap-3">
                  <Badge variant="outline">{row.status}</Badge>
                  {row.failureCode && <Badge variant="destructive">{row.failureCode}</Badge>}
                  {row.costMicroUsd === null ? `${usd(row.estimatedMicroUsd)} estimated` : usd(row.costMicroUsd)}
                </span>
              </button>
            ))}
          </div>
          {selected && (
            <Card>
              <CardHeader>
                <CardTitle>Request {selected.id}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-2 text-sm sm:grid-cols-3">
                  <span>
                    Developer:{" "}
                    {directoryData?.developers.find((item) => item.id === selected.subject)?.name ?? selected.subject}
                  </span>
                  <span>
                    Application:{" "}
                    {directoryData?.applications.find((item) => item.id === selected.app)?.name ?? selected.app}
                  </span>
                  <span>Provider: {selected.provider || "Unknown"}</span>
                  <span>Input tokens: {selected.inputTokens ?? "Unknown"}</span>
                  <span>Output tokens: {selected.outputTokens ?? "Unknown"}</span>
                  <span>Admission: {formatMs(selected.metrics?.admissionMs)}</span>
                  <span>Provider: {formatMs(selected.metrics?.providerMs)}</span>
                  <span>Settlement: {formatMs(selected.metrics?.settlementMs)}</span>
                  <span>Total: {formatMs(selected.metrics?.totalMs)}</span>
                  <span>Gateway: {formatMs(selected.metrics?.gatewayMs)}</span>
                  <span>Settled: {formatSettlement(selected.status, selected.settledAt)}</span>
                </div>
                {canManage && (selected.status === "reserved" || selected.status === "uncertain") && (
                  <form
                    className="space-y-3 rounded border p-3"
                    onSubmit={(event) => {
                      event.preventDefault()
                      const form = new FormData(event.currentTarget)
                      const reason = form.get("reason")
                      if (typeof reason !== "string") return
                      reconcile.mutate({
                        requestId: selected.id,
                        actualMicroUsd: Math.round(Number(form.get("actual")) * 1_000_000),
                        reason,
                      })
                    }}
                  >
                    <h3 className="font-medium">Reconcile pending charge</h3>
                    <p className="text-sm text-muted-foreground">
                      Verify the final provider charge before saving. This action is audited.
                    </p>
                    <label className="block space-y-2 text-sm">
                      Verified charge (USD)
                      <Input name="actual" type="number" min="0" step="0.000001" required />
                    </label>
                    <label className="block space-y-2 text-sm">
                      Evidence / reason
                      <Input name="reason" minLength={10} maxLength={1000} required />
                    </label>
                    <Button type="submit" disabled={reconcile.isLoading}>
                      Record verified charge
                    </Button>
                  </form>
                )}
                <div>
                  <h3 className="mb-2 font-medium">Prompt</h3>
                  <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-secondary p-3 text-sm">
                    {selected.prompt ?? "Not available"}
                  </pre>
                </div>
                <div>
                  <h3 className="mb-2 font-medium">Response</h3>
                  <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-secondary p-3 text-sm">
                    {selected.response ?? "Not available"}
                  </pre>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="policies" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Budget consumption below is the policy’s current UTC day/month total, independent of the selected date
            range.
          </p>
          {canManage && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Add budget</CardTitle>
              </CardHeader>
              <CardContent>
                <form className="grid gap-4 sm:grid-cols-3" onSubmit={createPolicy}>
                  <div className="space-y-2">
                    <Label>Budget applies to</Label>
                    <Select
                      value={newScope}
                      onValueChange={(value) => {
                        if (
                          value === "subject" ||
                          value === "application" ||
                          value === "environment" ||
                          value === "subject_application"
                        )
                          setNewScope(value)
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="subject">Developer across all apps</SelectItem>
                        <SelectItem value="application">Application</SelectItem>
                        <SelectItem value="subject_application">Developer + application</SelectItem>
                        <SelectItem value="environment">Entire environment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {policyScopeInputs}
                  <div className="space-y-2">
                    <Label>Environment</Label>
                    <Select
                      value={newEnvironment}
                      onValueChange={(value) => {
                        if (value === "dev" || value === "staging" || value === "prod") setNewEnvironment(value)
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ENVIRONMENTS.filter((value): value is Exclude<Environment, "all"> => value !== "all").map(
                          (value) => (
                            <SelectItem key={value} value={value}>
                              {value}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="space-y-2 text-sm">
                    Daily limit (USD)
                    <Input name="daily" type="number" min="0" step="0.01" defaultValue="20" required />
                  </label>
                  <label className="space-y-2 text-sm">
                    Monthly limit (USD)
                    <Input name="monthly" type="number" min="0" step="0.01" defaultValue="200" required />
                  </label>
                  <label className="space-y-2 text-sm">
                    Alert threshold (%)
                    <Input name="threshold" type="number" min="1" max="100" step="1" defaultValue="80" required />
                  </label>
                  <Button type="submit" disabled={savePolicy.isLoading}>
                    Create budget
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
          {policyRows.map((policy) => {
            const dailyPercent = policy.dailyLimitMicroUsd
              ? (100 * policy.dailyUsedMicroUsd) / policy.dailyLimitMicroUsd
              : 0
            const monthlyPercent = policy.monthlyLimitMicroUsd
              ? (100 * policy.monthlyUsedMicroUsd) / policy.monthlyLimitMicroUsd
              : 0
            const threshold = policy.alertThresholdPercent ?? 80
            return (
              <Card key={`${policy.scope}-${policy.scopeId}-${policy.environment}`}>
                <CardHeader>
                  <CardTitle className="text-base">
                    {policy.scope}:{" "}
                    {policyScopeLabel(policy, directoryData?.developers ?? [], directoryData?.applications ?? [])}{" "}
                    <Badge variant="outline">{policy.environment}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form
                    className="flex flex-wrap items-end gap-4"
                    onSubmit={(event) => {
                      event.preventDefault()
                      if (!canManage) return
                      const data = new FormData(event.currentTarget)
                      savePolicy.mutate({
                        ...policy,
                        dailyLimitMicroUsd: Math.round(Number(data.get("daily")) * 1_000_000),
                        monthlyLimitMicroUsd: Math.round(Number(data.get("monthly")) * 1_000_000),
                        alertThresholdPercent: Math.round(Number(data.get("threshold"))),
                      })
                    }}
                  >
                    <label className="space-y-2 text-sm">
                      Daily limit (USD)
                      <Input
                        name="daily"
                        type="number"
                        min="1"
                        step="0.01"
                        defaultValue={policy.dailyLimitMicroUsd / 1_000_000}
                        disabled={!canManage}
                        required
                      />
                    </label>
                    <label className="space-y-2 text-sm">
                      Monthly limit (USD)
                      <Input
                        name="monthly"
                        type="number"
                        min="0"
                        step="0.01"
                        defaultValue={policy.monthlyLimitMicroUsd / 1_000_000}
                        disabled={!canManage}
                        required
                      />
                    </label>
                    <label className="space-y-2 text-sm">
                      Alert threshold (%)
                      <Input
                        name="threshold"
                        type="number"
                        min="1"
                        max="100"
                        step="1"
                        defaultValue={threshold}
                        disabled={!canManage}
                        required
                      />
                    </label>
                    <div className="min-w-64 flex-1 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>
                          Today {usd(policy.dailyUsedMicroUsd)} used · {usd(policy.dailyRemainingMicroUsd)} remaining
                        </span>
                        <span>
                          {Math.round(dailyPercent)}%{dailyPercent >= threshold ? " · alert" : ""}
                        </span>
                      </div>
                      <div className="h-2 rounded bg-secondary">
                        <div
                          className={`h-full rounded ${dailyPercent >= threshold ? "bg-destructive" : "bg-primary"}`}
                          style={{ width: `${Math.min(100, dailyPercent)}%` }}
                        />
                      </div>
                      <div className="flex justify-between">
                        <span>
                          Month {usd(policy.monthlyUsedMicroUsd)} used · {usd(policy.monthlyRemainingMicroUsd)}{" "}
                          remaining
                        </span>
                        <span>
                          {Math.round(monthlyPercent)}%{monthlyPercent >= threshold ? " · alert" : ""}
                        </span>
                      </div>
                      <div className="h-2 rounded bg-secondary">
                        <div
                          className={`h-full rounded ${monthlyPercent >= threshold ? "bg-destructive" : "bg-primary"}`}
                          style={{ width: `${Math.min(100, monthlyPercent)}%` }}
                        />
                      </div>
                    </div>
                    <Badge variant="outline">{policy.enabled ? "Enabled" : "Access disabled"}</Badge>
                    {canManage && (
                      <>
                        <Button type="submit" disabled={savePolicy.isLoading}>
                          Save limits
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={savePolicy.isLoading}
                          onClick={() => savePolicy.mutate({ ...policy, enabled: !policy.enabled })}
                        >
                          {policy.enabled ? "Disable access" : "Enable access"}
                        </Button>
                      </>
                    )}
                  </form>
                </CardContent>
              </Card>
            )
          })}
        </TabsContent>
      </Tabs>
    </div>
  )
}
